import { Package, User } from 'lucide-react';
import { OrderItemCreateInput, RENTAL_LENGTH_OPTIONS } from '../../types/orderItem';
import type { OrderFormData } from '../CreateOrderWizard';
import { Badge } from '../ui/badge';

type ReviewOrderStepProps = {
  orderData: OrderFormData;
  items: OrderItemCreateInput[];
  total: number;
};

const ReviewOrderStep = ({
  orderData,
  items,
  total,
}: ReviewOrderStepProps) => {
  const getRentalLabel = (months: number) => {
    return RENTAL_LENGTH_OPTIONS.find(opt => opt.value === months)?.label || `${months} months`;
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-4">
      {/* Customer Information */}
      <div className="p-4 border rounded-lg bg-white space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <User className="h-5 w-5" />
          Customer Information
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">Customer Name</div>
            <div className="font-medium">{orderData.customer_name}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Email</div>
            <div className="font-medium">{orderData.customer_email}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Phone Number</div>
            <div className="font-medium">{orderData.customer_phone}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Order Date</div>
            <div className="font-medium">{orderData.order_date}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500">Required By</div>
            <div className="font-medium">{orderData.required_by_date || 'Not set'}</div>
          </div>
        </div>
        {orderData.special_instructions && (
          <div>
            <div className="text-sm text-gray-500">Special Instructions</div>
            <div className="font-medium">{orderData.special_instructions}</div>
          </div>
        )}
        <div>
          <div className="text-sm text-gray-500">Status</div>
          <Badge>{orderData.status.toUpperCase()}</Badge>
        </div>
      </div>

      {/* Order Items */}
      <div className="p-4 border rounded-lg bg-white space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Package className="h-5 w-5" />
          Order Items ({items.length})
        </h3>

        <div className="space-y-2">
          {items.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-4 p-3 border rounded-lg"
            >
              {/* Product Image */}
              {(item as any)._imageUrl ? (
                <div className="flex-shrink-0 w-16 h-16 bg-muted rounded-lg overflow-hidden">
                  <img
                    src={(item as any)._imageUrl}
                    alt={(item as any)._templateName || 'Product'}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="flex-shrink-0 w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                  <Package className="h-8 w-8 text-muted-foreground" />
                </div>
              )}

              {/* Product Details */}
              <div className="flex-1">
                <div className="font-medium">{(item as any)._templateName || `Item ${index + 1}`}</div>
                <div className="text-sm text-gray-500">
                  Qty: {item.quantity} × £{formatCurrency(item.unit_price)}/month | {item.rental_length_months ? getRentalLabel(item.rental_length_months) : 'Not specified'}
                </div>
              </div>

              {/* Price */}
              <div className="text-right flex-shrink-0">
                <div className="font-semibold">£{formatCurrency(item.quantity * item.unit_price * item.rental_length_months)}</div>
                <div className="text-xs text-gray-500">Total revenue</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-between items-center pt-3 border-t font-bold text-lg">
          <span>Total:</span>
          <span className="text-2xl text-primary">£{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );
};

export default ReviewOrderStep;
